import { fetch } from '@/services/fetch'

// Mints a fresh Cleverbase authorize URL (single-use, 1h signed-state TTL).
// `returnTo` is carried through Cleverbase's signed OAuth `state` so the
// chain's terminal redirect lands back in backoffice (see
// CleverbaseRedirectPaths::resolveReturnUrl()).
const refreshSigningLink = async (jobIri: string, returnTo: string): Promise<JobEntity> => {
  return await fetch(
    `${jobIri}/qes/refresh_signing_link?returnTo=${encodeURIComponent(returnTo)}`,
    { method: 'post' }
  )
}

// Finishes a partially signed submission (`qesStatus: 'partially_signed'`):
// the signatures were already issued, only some PDFs still need embedding, so no
// new Cleverbase approval is involved. A 409 carries a `detail` naming what still
// can't be finished.
const finalizeSigning = async (jobIri: string): Promise<JobEntity> => {
  return await fetch(`${jobIri}/qes/finalize_signing`, { method: 'post' })
}

// Discards a partially signed attempt that can never be finished
// (`qesFinalizeBlockedReason`) and resets the submission, so "Sign here"
// starts a fresh attempt. Internal users only.
const abandonSigning = async (jobIri: string): Promise<JobEntity> => {
  return await fetch(`${jobIri}/qes/abandon_signing`, { method: 'post' })
}

// Declines the signing job before ever being redirected to a signing
// provider — same endpoint and payload as lsp-new's review screen.
const declineSigning = async (jobId: string, reason: string): Promise<JobEntity> => {
  return await fetch('/jobs/' + jobId + '/qes/decline', {
    method: 'post',
    data: { reason }
  })
}

// For in-page preview — the download endpoints default to
// `Content-Disposition: attachment` unless asked for `inline` explicitly.
const fetchFileBlob = async (fileIri: string): Promise<Blob> => {
  const separator = fileIri.includes('?') ? '&' : '?'

  return await fetch(fileIri + separator + 'disposition=inline', { responseType: 'blob' }, false)
}

// Background status check (no global loading indicator) — used to notice when
// the asynchronous post-signing processing has moved a signing job on.
const fetchJob = async (jobIri: string): Promise<JobEntity> => {
  return await fetch(jobIri, { method: 'GET' }, false)
}

export {
  refreshSigningLink,
  finalizeSigning,
  abandonSigning,
  declineSigning,
  fetchFileBlob,
  fetchJob
}
